import { AuthLinkInfo, PASSWORD_MIN, SessionInfo } from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { PasswordInput } from '../components/PasswordInput';
import { Alert } from '../components/States';
import { api } from '../lib/api';
import { useApplySession } from '../lib/session';
import { AuthLayout } from './AuthLayout';

/** /convite/:token and /redefinir-senha/:token: choose a password from a one-time link. */
export function SetPasswordPage({ kind }: { kind: 'invite' | 'password_reset' }) {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const apply = useApplySession();
  const link = useQuery({
    queryKey: ['auth-link', token],
    queryFn: () =>
      api.get(`/auth/links/${encodeURIComponent(token)}`, AuthLinkInfo, { quiet401: true }),
    retry: false,
  });
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.post(
        kind === 'invite' ? '/auth/accept-invite' : '/auth/reset-password',
        SessionInfo,
        { token, password, ...(kind === 'invite' && name ? { name } : {}) },
        { quiet401: true },
      ),
    onSuccess: (s) => {
      apply(s);
      void navigate('/', { replace: true });
    },
  });

  const short = touched && password.length < PASSWORD_MIN;
  const mismatch = touched && !short && password !== confirm;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (password.length < PASSWORD_MIN || password !== confirm) return;
    save.mutate();
  };

  if (link.isPending) {
    return (
      <AuthLayout>
        <div className="card pop" style={{ width: '100%', padding: 28 }} aria-busy="true">
          <div className="skel" style={{ height: 120 }} />
        </div>
      </AuthLayout>
    );
  }

  if (link.isError) {
    return (
      <AuthLayout>
        <div className="card pop col" style={{ width: '100%', gap: 18, padding: 28 }}>
          <h1 style={{ fontSize: 24 }}>Link inválido</h1>
          <Alert tone="err">
            <b>Este link expirou ou já foi usado.</b> Peça ao Admin um link novo.
          </Alert>
          <Link className="btn g block" to="/login">
            Ir para entrar
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const info = link.data;
  return (
    <AuthLayout>
      <form
        className="card pop col"
        style={{ width: '100%', gap: 18, padding: 28 }}
        onSubmit={submit}
        noValidate
      >
        <div className="col" style={{ gap: 4 }}>
          <h1 style={{ fontSize: 24 }}>
            {kind === 'invite' ? `Boas-vindas, ${info.name}` : 'Criar senha nova'}
          </h1>
          <p className="muted sm">{info.email}</p>
        </div>
        {kind === 'invite' ? (
          <div className="field">
            <label htmlFor="nm">Seu nome</label>
            <input
              id="nm"
              className="input"
              value={name ?? info.name}
              onChange={(e) => setName(e.target.value)}
            />
            <span className="help">Como aparece no sistema.</span>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="pw">Senha nova</label>
          <PasswordInput
            id="pw"
            value={password}
            onChange={setPassword}
            invalid={short}
            autoComplete="new-password"
          />
          {short ? (
            <span className="help err">
              <Icon name="alert" size={14} />
              Use pelo menos {PASSWORD_MIN} caracteres.
            </span>
          ) : (
            <span className="help">
              Pelo menos {PASSWORD_MIN} caracteres. Uma frase curta é fácil de lembrar.
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="pw2">Repita a senha</label>
          <PasswordInput
            id="pw2"
            value={confirm}
            onChange={setConfirm}
            invalid={mismatch}
            autoComplete="new-password"
          />
          {mismatch ? (
            <span className="help err">
              <Icon name="alert" size={14} />
              As senhas não são iguais.
            </span>
          ) : null}
        </div>
        {save.isError ? (
          <Alert tone="err">
            <b>Não foi possível salvar.</b> O link pode ter expirado; peça outro ao Admin.
          </Alert>
        ) : null}
        <button type="submit" className="btn p block" disabled={save.isPending}>
          {kind === 'invite' ? 'Criar senha e entrar' : 'Salvar senha e entrar'}
        </button>
      </form>
    </AuthLayout>
  );
}
